import ViewList, { ViewListItem } from "@/components/layout/ViewList";
import { useViewList, useViewListContext } from "@/context/ViewListContext";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "@vixi/localization";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import _ from "lodash";
import {
  AlternatingMessages,
  Button,
  cn,
  CourseDetailsForm,
  CourseEditor,
  DemographicFormValues,
  demographicValidationSchema,
  Loader,
  MainDetailsFormValues,
  mainDetailsValidationSchema,
  ManageAccessForm,
  PageMainContent,
  useCourseEditorContext,
  useGoBack,
} from "@vixi/ui";
import { CourseDraftSaved } from "../CourseDraftSaved";
import { CourseData, COURSE_TYPE } from "@vixi/types";
import { assert, courseToSavedCourse, logger } from "@vixi/utils";
import { useSaveCourse } from "@/hooks/api/course/useSaveCourse";
import { useQueryClient } from "@tanstack/react-query";

interface CourseReviewStepProps {
  saveCourseMutation: ReturnType<typeof useSaveCourse>;
  prevValue?: { bookInfo?: { about?: string; audience?: string; tone?: string } } | null;
}

const btnFontSize = "ar:fs-16 fs-21";

export const CourseReviewStep: FC<CourseReviewStepProps> = ({ saveCourseMutation, prevValue }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const parentViewList = useViewListContext();
  const viewList = useViewList();

  // Add goBack for manage access step
  const goBack = useMemo(
    () => (viewList.activeStep !== 2 ? null : viewList.prevStep),
    [viewList.activeStep],
  );
  useGoBack(goBack, {
    allowChanges: true,
  });

  const [savedToDraft, setSavedToDraft] = useState(false);
  const saveToDraft = () => {
    setSavedToDraft(true);
  };

  const { courseData: course } = useCourseEditorContext();
  const onCourseDetailsComplete = useCallback(() => {
    viewList.nextStep();
  }, [course]);

  const { courseData } = useCourseEditorContext();

  // Details Forms
  const mainDetailsForm = useForm<MainDetailsFormValues>({
    mode: "onChange",
    defaultValues: {
      courseName: "",
      courseDescription: "",
      authorDescription: "",
    },
    resolver: zodResolver(mainDetailsValidationSchema),
  });

  const demographicForm = useForm<DemographicFormValues>({
    defaultValues: {
      category: undefined,
      accessBy: null,
    },
    resolver: zodResolver(demographicValidationSchema),
  });

  const {
    formState: { isValid: demographicFormValid, errors: _demographicErrors },
  } = demographicForm;

  // Save Course
  const { mutateAsync: saveCourse, isError, error, isPending } = saveCourseMutation;

  const onSubmit = async () => {
    assert(!!courseData, "Course is not defined");
    assert(courseData.chapters !== undefined, "Course has no chapters");

    const selectedCategory = demographicForm.getValues("category");
    assert(!!selectedCategory, "Category is not defined");

    // Ensure required metadata fields are present
    // If missing, try to load from localStorage (for jobs loaded from card click)
    // Or use prevValue as fallback (original form values)
    let eBookAbout = courseData.eBookAbout;
    let targetAudience = courseData.targetAudience;
    let conversationTone = courseData.conversationTone;

    logger.info("🔍 CourseReviewStep - Current courseData metadata:", {
      eBookAbout: !!eBookAbout,
      targetAudience: !!targetAudience,
      conversationTone: !!conversationTone,
    });

    // If metadata is missing, try to load from localStorage or prevValue
    if (!eBookAbout || !targetAudience || !conversationTone) {
      // First, try prevValue (original form values) as fallback
      if (prevValue?.bookInfo) {
        logger.info("🔍 Trying to load metadata from prevValue:", {
          about: !!prevValue.bookInfo.about,
          audience: !!prevValue.bookInfo.audience,
          tone: !!prevValue.bookInfo.tone,
        });
        if (!eBookAbout && prevValue.bookInfo.about) eBookAbout = prevValue.bookInfo.about;
        if (!targetAudience && prevValue.bookInfo.audience) targetAudience = prevValue.bookInfo.audience;
        if (!conversationTone && prevValue.bookInfo.tone) conversationTone = prevValue.bookInfo.tone;
      }
      const TEMP_ACTIVE_JOB_ID_KEY = "temp-active-job-id";
      const JOB_COURSE_DATA_STORAGE_KEY = "course-generation-job-course-data";
      
      logger.info("⚠️ Metadata missing, searching localStorage...");
      
      try {
        // Try multiple sources for jobId:
        // 1. temp-active-job-id (saved when user clicked on card)
        // 2. URL search params (if user navigated with jobId)
        // 3. course-generation-job-id (active job)
        let jobId: string | null = localStorage.getItem(TEMP_ACTIVE_JOB_ID_KEY);
        
        // If no temp jobId, try URL search params
        if (!jobId && typeof window !== 'undefined') {
          try {
            const urlParams = new URLSearchParams(window.location.search);
            jobId = urlParams.get("jobId");
          } catch (error) {
            // Ignore URL parsing errors
          }
        }
        
        // If still no jobId, try active jobId
        if (!jobId) {
          jobId = localStorage.getItem("course-generation-job-id");
        }
        
        logger.info("🔍 Found jobId from:", {
          tempActive: !!localStorage.getItem(TEMP_ACTIVE_JOB_ID_KEY),
          url: typeof window !== 'undefined' ? !!window.location.search : false,
          activeJob: !!localStorage.getItem("course-generation-job-id"),
          finalJobId: jobId,
        });
        
        if (jobId) {
          // Load metadata for specific jobId
          const metadataKey = `${JOB_COURSE_DATA_STORAGE_KEY}-${jobId}`;
          const storedData = localStorage.getItem(metadataKey);
          logger.info("🔍 Loading metadata for jobId:", jobId, "Key:", metadataKey, "Found:", !!storedData);
          
          if (storedData) {
            try {
              const parsed = JSON.parse(storedData);
              logger.info("🔍 Parsed metadata from localStorage:", {
                jobId: parsed.jobId,
                eBookAbout: parsed.eBookAbout || "MISSING",
                targetAudience: parsed.targetAudience || "MISSING",
                conversationTone: parsed.conversationTone || "MISSING",
                hasAllFields: !!(parsed.eBookAbout && parsed.targetAudience && parsed.conversationTone),
              });
              
              if (!eBookAbout && parsed.eBookAbout) {
                eBookAbout = parsed.eBookAbout;
                logger.info("✅ Loaded eBookAbout from localStorage");
              }
              if (!targetAudience && parsed.targetAudience) {
                targetAudience = parsed.targetAudience;
                logger.info("✅ Loaded targetAudience from localStorage");
              }
              if (!conversationTone && parsed.conversationTone) {
                conversationTone = parsed.conversationTone;
                logger.info("✅ Loaded conversationTone from localStorage");
              }
            } catch (error) {
              logger.error("❌ Failed to parse metadata from localStorage:", error, "Raw data:", storedData);
            }
          } else {
            logger.warn("⚠️ No metadata found in localStorage for key:", metadataKey);
            // List all localStorage keys that start with JOB_COURSE_DATA_STORAGE_KEY
            const allKeys: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith(JOB_COURSE_DATA_STORAGE_KEY)) {
                allKeys.push(key);
              }
            }
            logger.info("📋 Available metadata keys in localStorage:", allKeys);
          }
        }
        
        // If still missing, search all localStorage entries
        if (!eBookAbout || !targetAudience || !conversationTone) {
          logger.info("⚠️ Still missing metadata, searching all localStorage entries...");
          
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(`${JOB_COURSE_DATA_STORAGE_KEY}-`)) {
              try {
                const storedData = localStorage.getItem(key);
                if (storedData) {
                  const parsed = JSON.parse(storedData);
                  logger.info("🔍 Checking localStorage entry:", {
                    key,
                    hasEBookAbout: !!parsed.eBookAbout,
                    hasTargetAudience: !!parsed.targetAudience,
                    hasConversationTone: !!parsed.conversationTone,
                    parsedKeys: Object.keys(parsed),
                  });
                  
                  // Use first entry that has metadata
                  if (parsed.eBookAbout || parsed.targetAudience || parsed.conversationTone) {
                    logger.info("🔍 Found metadata in key:", key, "Values:", {
                      eBookAbout: parsed.eBookAbout?.substring(0, 50) || "MISSING",
                      targetAudience: parsed.targetAudience?.substring(0, 50) || "MISSING",
                      conversationTone: parsed.conversationTone?.substring(0, 50) || "MISSING",
                    });
                    if (!eBookAbout && parsed.eBookAbout) {
                      eBookAbout = parsed.eBookAbout;
                      logger.info("✅ Loaded eBookAbout from:", key);
                    }
                    if (!targetAudience && parsed.targetAudience) {
                      targetAudience = parsed.targetAudience;
                      logger.info("✅ Loaded targetAudience from:", key);
                    }
                    if (!conversationTone && parsed.conversationTone) {
                      conversationTone = parsed.conversationTone;
                      logger.info("✅ Loaded conversationTone from:", key);
                    }
                    
                    // If we found all metadata, stop searching
                    if (eBookAbout && targetAudience && conversationTone) {
                      logger.info("✅ Found all metadata, stopping search");
                      break;
                    }
                  }
                }
              } catch (error) {
                logger.error("❌ Error parsing localStorage entry:", key, error);
                // Skip invalid entries
                continue;
              }
            }
          }
        }
      } catch (error) {
        logger.error("Failed to load metadata from localStorage:", error);
      }
    }

    logger.info("🔍 Final metadata before save:", {
      eBookAbout: !!eBookAbout,
      targetAudience: !!targetAudience,
      conversationTone: !!conversationTone,
    });

    // Check if required fields are present - if not, show user-friendly error
    if (!targetAudience?.length || !conversationTone?.length || !eBookAbout?.length) {
      logger.error("❌ Missing required metadata fields:", {
        eBookAbout: !!eBookAbout,
        targetAudience: !!targetAudience,
        conversationTone: !!conversationTone,
      });
      
      // Show user-friendly error message
      alert(
        "Missing course information. Please go back and fill in the course details (About, Target Audience, Conversation Tone) before saving."
      );
      
      // Navigate back to first step to fill in metadata
      if (viewList.goToStep) {
        viewList.goToStep(0);
      } else if (viewList.prevStep) {
        // Go back multiple steps if goToStep is not available
        viewList.prevStep();
      }
      return;
    }

    // Ensure metadata is set - use loaded values or fallback to courseData
    const finalEBookAbout = eBookAbout || courseData.eBookAbout;
    const finalTargetAudience = targetAudience || courseData.targetAudience;
    const finalConversationTone = conversationTone || courseData.conversationTone;
    
    logger.info("🔍 Final metadata values before creating course object:", {
      eBookAbout: finalEBookAbout || "MISSING",
      targetAudience: finalTargetAudience || "MISSING",
      conversationTone: finalConversationTone || "MISSING",
    });
    
    const course: CourseData.Course = {
      ...courseData,
      courseType: courseData.courseType ?? COURSE_TYPE.EBOOK, // Default to EBOOK if not set
      title: mainDetailsForm.getValues("courseName"),
      description: mainDetailsForm.getValues("courseDescription"),
      aboutTheAuthor: mainDetailsForm.getValues("authorDescription"),
      category: { id: selectedCategory },
      // Ensure metadata is included
      eBookAbout: finalEBookAbout,
      targetAudience: finalTargetAudience,
      conversationTone: finalConversationTone,
    };
    
    logger.info("🔍 Course object before save:", {
      hasEBookAbout: !!course.eBookAbout,
      hasTargetAudience: !!course.targetAudience,
      hasConversationTone: !!course.conversationTone,
      eBookAbout: course.eBookAbout?.substring(0, 50) || "MISSING",
      targetAudience: course.targetAudience?.substring(0, 50) || "MISSING",
      conversationTone: course.conversationTone?.substring(0, 50) || "MISSING",
    });

    logger.info(
      "Saving course",
      courseToSavedCourse(course, {
        ignoreId: true,
      }),
    );

    logger.info("🔍 parentViewList:", parentViewList);
    logger.info("🔍 parentViewList?.nextStep:", parentViewList?.nextStep);

    // Move to loading step first (step 2 in CourseReviewStep's ViewList)
    viewList.nextStep();

    // Wait for save to complete, then move to next step in parent flow
    saveCourse({ course })
      .then(() => {
        logger.info("✅ Course saved successfully", courseToSavedCourse(course, { ignoreId: true }));
        
        // Remove card using temp-active-job-id (saved when user clicked on card)
        // Also check URL and localStorage for jobId as fallback
        const TEMP_ACTIVE_JOB_ID_KEY = "temp-active-job-id";
        const JOB_COURSE_DATA_STORAGE_KEY = "course-generation-job-course-data";
        const JOB_ID_STORAGE_KEY = "course-generation-job-id";
        
        // Try multiple sources for jobId:
        // 1. temp-active-job-id (saved when user clicked on card)
        // 2. URL search params (if user navigated with jobId)
        // 3. course-generation-job-id (active job)
        let jobIdToRemove: string | null = null;
        
        try {
          // First, try temp active jobId
          jobIdToRemove = localStorage.getItem(TEMP_ACTIVE_JOB_ID_KEY);
          
          // If no temp jobId, try URL search params
          if (!jobIdToRemove && typeof window !== 'undefined') {
            try {
              const urlParams = new URLSearchParams(window.location.search);
              jobIdToRemove = urlParams.get("jobId");
            } catch (error) {
              // Ignore URL parsing errors
            }
          }
          
          // If still no jobId, try active jobId
          if (!jobIdToRemove) {
            jobIdToRemove = localStorage.getItem(JOB_ID_STORAGE_KEY);
          }
          
          logger.info("🔍 Looking for jobId to remove card:", {
            tempActive: !!localStorage.getItem(TEMP_ACTIVE_JOB_ID_KEY),
            url: typeof window !== 'undefined' ? !!window.location.search : false,
            activeJob: !!localStorage.getItem(JOB_ID_STORAGE_KEY),
            finalJobId: jobIdToRemove,
          });
          
          if (jobIdToRemove) {
            // Remove course metadata for this specific jobId
            const metadataKey = `${JOB_COURSE_DATA_STORAGE_KEY}-${jobIdToRemove}`;
            localStorage.removeItem(metadataKey);
            localStorage.removeItem(TEMP_ACTIVE_JOB_ID_KEY); // Clear temp jobId
            localStorage.removeItem(JOB_ID_STORAGE_KEY); // Clear active jobId
            logger.info(`🗑️ Removed card for jobId: ${jobIdToRemove}`);
            logger.info(`🗑️ Removed metadata key: ${metadataKey}`);
          } else {
            logger.warn("⚠️ No jobId found in localStorage or URL to remove card");
          }
        } catch (error) {
          logger.error("❌ Failed to remove card:", error);
        }
        
        // Remove course metadata from localStorage to hide the card
        // We search for metadata that matches this course's data (fallback method)
        try {
          // Find and remove all metadata entries that match this course
          // We match by eBookAbout, targetAudience, and conversationTone
          const courseIdentifier = `${course.eBookAbout || ''}-${course.targetAudience || ''}-${course.conversationTone || ''}`.toLowerCase().trim();
          
          logger.info("🔍 Course identifier for matching:", courseIdentifier);
          logger.info("🔍 Course data:", {
            eBookAbout: course.eBookAbout,
            targetAudience: course.targetAudience,
            conversationTone: course.conversationTone,
          });
          
          if (courseIdentifier && courseIdentifier.length > 0) {
            // Search through localStorage for matching entries
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith(`${JOB_COURSE_DATA_STORAGE_KEY}-`)) {
                try {
                  const storedData = localStorage.getItem(key);
                  if (storedData) {
                    const parsed = JSON.parse(storedData);
                    const storedIdentifier = `${parsed.eBookAbout || ''}-${parsed.targetAudience || ''}-${parsed.conversationTone || ''}`.toLowerCase().trim();
                    
                    // Use more flexible matching - normalize whitespace and compare
                    const normalizedCourseId = courseIdentifier.replace(/\s+/g, ' ').trim();
                    const normalizedStoredId = storedIdentifier.replace(/\s+/g, ' ').trim();
                    
                    logger.info(`🔍 Checking localStorage key: ${key}`);
                    logger.info(`🔍 Stored identifier: "${normalizedStoredId}"`);
                    logger.info(`🔍 Course identifier: "${normalizedCourseId}"`);
                    logger.info(`🔍 Exact match: ${normalizedStoredId === normalizedCourseId}`);
                    
                    // Match if identifiers are equal (after normalization)
                    if (normalizedStoredId === normalizedCourseId && normalizedStoredId.length > 0) {
                      keysToRemove.push(key);
                      logger.info(`✅ Found matching entry: ${key}`);
                    }
                  }
                } catch (error) {
                  // Skip invalid entries
                  logger.warn(`⚠️ Failed to parse localStorage entry ${key}:`, error);
                  continue;
                }
              }
            }
            
            // Remove all matching entries
            keysToRemove.forEach((key) => {
              localStorage.removeItem(key);
              logger.info(`🗑️ Removed course metadata from localStorage: ${key}`);
            });
            
            if (keysToRemove.length > 0) {
              logger.info(`✅ Removed ${keysToRemove.length} course metadata entry/entries from localStorage`);
            } else {
              logger.warn(`⚠️ No matching metadata entries found in localStorage to remove`);
              logger.warn(`⚠️ This may cause the card to remain visible until page refresh`);
            }
          } else {
            logger.warn(`⚠️ Course identifier is empty, cannot match with localStorage`);
          }
        } catch (error) {
          logger.error("❌ Failed to remove course metadata from localStorage:", error);
        }
        
        // Invalidate courses query to refresh the saved courses list
        // This ensures the card disappears immediately after saving
        // Use prefix matching to invalidate all course queries
        // Wait a bit to ensure backend has saved the course before refetching
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["courses"], exact: false });
          queryClient.invalidateQueries({ queryKey: ["course-jobs"] });
          logger.info("🔄 Invalidated courses and jobs queries to refresh card visibility");
          
          // Also refetch immediately to ensure card disappears right away
          queryClient.refetchQueries({ queryKey: ["courses"], exact: false });
          queryClient.refetchQueries({ queryKey: ["course-jobs"] });
          logger.info("🔄 Refetched courses and jobs queries immediately");
        }, 500); // Wait 500ms for backend to save
        
        logger.info("🔍 Calling parentViewList?.nextStep()");
        if (parentViewList?.nextStep) {
          parentViewList.nextStep();
          logger.info("✅ parentViewList.nextStep() called successfully");
        } else {
          logger.error("❌ parentViewList.nextStep is not available!");
          // Fallback: try to use viewList's parent
          if (viewList.parent?.nextStep) {
            logger.info("🔄 Using viewList.parent.nextStep() as fallback");
            viewList.parent.nextStep();
          } else {
            logger.error("❌ No parent nextStep available!");
          }
        }
      })
      .catch((error) => {
        logger.error("❌ Failed to save course:", error);
        // Go back to CourseDetails form on error
        viewList.prevStep();
      });
  };

  return !savedToDraft ? (
    <ViewList {...viewList} debug="CourseReviewStep">
      {/* Course Editor */}
      <ViewListItem >
        <CourseEditor
          navGroup={
            <div className="flex flex-col">
              <div className="mt-auto flex gap-6.25">
                <Button
                  disabled
                  onClick={saveToDraft}
                  className={cn(btnFontSize)}
                  variant="outlined"
                  size="small"
                >
                  {t("Action.SaveToDraft")}
                </Button>
                <Button
                  className={cn(btnFontSize)}
                  onClick={() => {
                    viewList.nextStep();
                  }}
                  variant="contained"
                  size="small"
                >
                  {t("Action.Next")}
                </Button>
              </div>
            </div>
          }
        />
      </ViewListItem>

      {/* Course Details */}
      <ViewListItem >
        <CourseDetailsForm
          mainDetailsForm={mainDetailsForm}
          demographicForm={demographicForm}
          onBack={viewList.prevStep}
          
          submitButton={(valid) => (
            <Button 
              size="large" 
              variant="contained" 
              color="primary" 
              onClick={onSubmit}
              disabled={!valid}
            >
              {t("Action.SubmitForReview")}
            </Button>
          )}
        />
      </ViewListItem>

      {/* Manage Access */}
      {/* 
      <ViewListItem>
        <PageMainContent>
          <h3 className="ps-5">{t("CourseDetailsForm.AccessByLabel")}</h3>
          <div className="min-h-0 flex-1">
            <ManageAccessForm form={demographicForm} />
          </div>
          <div className="fixed end-25 bottom-12.5 flex gap-10">
            {demographicFormValid && (
              <>
                <Button size="small" variant="outlined" className="self-center">
                  {t("Action.SaveToDraft")}
                </Button>
                <Button size="large" variant="contained" color="primary" onClick={onSubmit}>
                  {t("Action.SubmitForReview")}
                </Button>
              </>
            )}
          </div>
        </PageMainContent>
      </ViewListItem>
      */}

      {/* Loading */}
      <ViewListItem>
        <div className="mx-auto my-auto text-center">
          <Loader loadingText="LoadingMessage.SavingYourCourse" />
          <AlternatingMessages messages={["LoadingMessage.Mins3"]} />
        </div>
      </ViewListItem>
    </ViewList>
  ) : (
    <CourseDraftSaved />
  );
};
